export const money = (pence: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: pence % 100 ? 2 : 0 }).format(pence / 100);

/**
 * What a booking settled in person still owes: promised at the check-in desk ("Pay later"),
 * unpaid after the event, or due on the day of an event still to come.
 */
export function unpaidText(b: { price_pence: number; desk_note?: string | null }, startsAt: string | null | undefined): string {
  if (b.desk_note?.startsWith('Pay later')) return `Pay later: ${money(b.price_pence)} owed`;
  if (startsAt && new Date(startsAt) < new Date()) return `${money(b.price_pence)} owed`;
  return `To pay on the day: ${money(b.price_pence)}`;
}

export const date = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso)) : '';

export const dateTime = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(iso));

/** Same scale as grade_label() in the database: 1 = 10th kyu ... 10 = 1st kyu, 11 = 1st dan ... 20 = 10th dan. */
export function gradeLabel(rank: number | null | undefined): string {
  if (!rank) return 'Ungraded';
  const ord = (n: number) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`;
  return rank <= 10 ? `${ord(11 - rank)} kyu` : `${ord(rank - 10)} dan`;
}

export const GRADES = Array.from({ length: 21 }, (_, rank) => ({ rank, label: gradeLabel(rank) }));

/** EGKA's bands from the Gasshuku form: Junior 13 and under, Cadet 14-17, Adult 18+. Same rule as age_band_on(). */
export function ageBand(dob: string | null | undefined, on = new Date()): 'junior' | 'cadet' | 'adult' | null {
  if (!dob) return null;
  const b = new Date(dob);
  let age = on.getFullYear() - b.getFullYear();
  if (on.getMonth() < b.getMonth() || (on.getMonth() === b.getMonth() && on.getDate() < b.getDate())) age--;
  return age <= 13 ? 'junior' : age <= 17 ? 'cadet' : 'adult';
}

/** "Junior", "Cadet" or "Adult", optionally on a given date (an event's, for gradings and bookings). */
export function ageBandLabel(dob: string | null | undefined, on?: string): string | null {
  const b = ageBand(dob, on ? new Date(on) : undefined);
  return b && b[0].toUpperCase() + b.slice(1);
}

/**
 * A dojo instructor's role in words. EGKA has one Chief Instructor for the whole
 * country, so a dojo's own head is its lead instructor. (Stored as 'chief' / 'assistant'.)
 */
export const instructorRole = (role: string | null | undefined) => role === 'chief' ? 'Lead instructor' : 'Instructor';
